import { useNavigate } from 'react-router-dom'
import { Area, AreaChart } from 'recharts'
import { ArrowDown, ArrowUp, Minus } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { cn } from '@/lib/utils'

export interface SingleSourceRailProps {
  data: {
    current: number
    prior: number
    daily: Array<{ date: string; pct: number }>
  }
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

const TITLE = 'Single-source share'
const TARGET_HREF = '/stories?confidence=single-source'
const SPARK_WIDTH = 160
const SPARK_HEIGHT = 48

function roundPp(n: number): number {
  return Math.round(n * 10) / 10
}

/**
 * Big-number + 14-day sparkline risk indicator for the SP4 Analytics page
 * (spec §4.2 ③). The card itself is the clickable target (role=button,
 * focusable) and always navigates to `/stories?confidence=single-source`.
 *
 * Palette: the rail is always warning-coloured (`--chart-warning`) because
 * a high single-source share is itself the signal. The delta pill flips
 * colour by direction — rising single-source share is BAD (warning),
 * falling is GOOD (success), flat is muted.
 */
export function SingleSourceRail({
  data,
  isLoading,
  isError,
  onRetry,
}: SingleSourceRailProps) {
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{TITLE}</h3>
        <Skeleton
          data-testid="sp4-single-source-skeleton"
          className="h-20 w-full"
        />
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{TITLE}</h3>
        <ErrorState
          title="Couldn't load single-source data."
          onRetry={onRetry}
        />
      </Card>
    )
  }

  if (data.daily.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="mb-3 text-sm font-semibold">{TITLE}</h3>
        <p className="text-sm text-muted-foreground">
          No single-source data in range.
        </p>
      </Card>
    )
  }

  const delta = roundPp(data.current - data.prior)
  const deltaDir: 'up' | 'down' | 'flat' =
    delta === 0 ? 'flat' : delta > 0 ? 'up' : 'down'
  // Rising single-source share is BAD → warning; falling is GOOD → success.
  const deltaColor =
    deltaDir === 'up'
      ? 'text-[color:var(--chart-warning)]'
      : deltaDir === 'down'
        ? 'text-[color:var(--chart-success)]'
        : 'text-muted-foreground'
  const DeltaIcon =
    deltaDir === 'up' ? ArrowUp : deltaDir === 'down' ? ArrowDown : Minus
  const deltaSign = delta > 0 ? '+' : delta < 0 ? '−' : ''
  const deltaLabel = `${deltaSign}${Math.abs(delta)}pp vs prior`

  const handleActivate = () => navigate(TARGET_HREF)

  return (
    <Card
      data-testid="sp4-single-source-card"
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleActivate()
        }
      }}
      aria-label={`Single-source share: ${roundPp(data.current)} percent, ${deltaLabel}. Open story list.`}
      className={cn(
        'cursor-pointer p-4 transition-colors',
        'hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <h3 className="mb-3 text-sm font-semibold">{TITLE}</h3>
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <div
            className="text-3xl font-bold tabular-nums text-[color:var(--chart-warning)]"
            aria-hidden
          >
            {roundPp(data.current)}%
          </div>
          <div
            className={cn('mt-1 flex items-center gap-1 text-xs', deltaColor)}
          >
            <DeltaIcon className="h-3 w-3" aria-hidden />
            <span>{deltaLabel}</span>
          </div>
        </div>
        <div
          data-testid="sp4-single-source-sparkline"
          className="shrink-0"
          aria-hidden
        >
          <AreaChart
            width={SPARK_WIDTH}
            height={SPARK_HEIGHT}
            data={data.daily}
            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          >
            <Area
              type="monotone"
              dataKey="pct"
              stroke="var(--chart-warning)"
              fill="var(--chart-warning)"
              fillOpacity={0.2}
              strokeWidth={2}
              isAnimationActive={false}
              dot
            />
          </AreaChart>
        </div>
      </div>
    </Card>
  )
}
